import { jsx as _jsx } from "react/jsx-runtime";
import { cn } from "@stackje/shared-components/lib/utils";
import { calculateScaledDimensions, usePreviewHeight } from "@stackje/shared-components/lib/preview-utils";
import { useEffect, useRef, useState } from "react";
export function ScaledPreviewContainer({ children, className, onClick, interactive = false, }) {
    const containerRef = useRef(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const [viewportDimensions, setViewportDimensions] = useState({ width: 0, height: 0 });
    const { height } = usePreviewHeight();
    // Calculate scale factors for both axes
    const scaleX = containerDimensions.width / viewportDimensions.width;
    const scaleY = containerDimensions.height / viewportDimensions.height;
    // Use the smaller scale to maintain aspect ratio
    const scale = Math.min(scaleX, scaleY);
    const scaledDimensions = calculateScaledDimensions(scale, viewportDimensions.width, viewportDimensions.height);
    // Update container and viewport dimensions
    useEffect(() => {
        const updateDimensions = () => {
            // Update container dimensions
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setContainerDimensions({
                    width: rect.width || 0,
                    height: rect.height || 0,
                });
            }
            // Update viewport dimensions
            setViewportDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        // Initial measurement
        setTimeout(updateDimensions, 0);
        const resizeObserver = new ResizeObserver(updateDimensions);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        // Listen for viewport changes
        window.addEventListener('resize', updateDimensions);
        // Listen for iOS Safari visual viewport changes
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', updateDimensions);
        }
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateDimensions);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', updateDimensions);
            }
        };
    }, []);
    return (_jsx("div", { ref: containerRef, className: cn("relative", "w-[90vw]", className, interactive && "cursor-pointer active:scale-[0.9] active:opacity-80 transition-all duration-200 transform-gpu"), style: {
            height,
        }, children: _jsx("div", { className: "absolute origin-top-left select-none bg-background border shadow-xl overflow-hidden", style: {
                width: `${viewportDimensions.width}px`,
                height: `${viewportDimensions.height}px`,
                transform: `scale(${scale})`,
                left: `${(containerDimensions.width - scaledDimensions.width) / 2}px`,
            }, onClick: onClick, onKeyDown: interactive ? (e) => {
                if ((e.key === "Enter" || e.key === " ") && onClick) {
                    onClick();
                }
            } : undefined, role: interactive ? "button" : undefined, tabIndex: interactive ? 0 : undefined, children: _jsx("div", { className: "h-full w-full pointer-events-none", children: children }) }) }));
}
